import type {
  AgreementAuthorizedEntityResponse,
  AgreementProductResponse,
  AgreementResponse,
  AgreementStatus as AgreementStatusV1,
  DemandChargeAllocationConfiguration,
  EntitySelection,
  Payment,
  PeakSaverConfiguration,
  ProductResponse,
  VolumePricing
} from '../api/client/billing'

// --- UI-only (no API equivalent) ---

export const PAYMENT_TERMINAL_STATUSES = ['ONLINE', 'OFFLINE'] as const
export type PaymentTerminalStatus = (typeof PAYMENT_TERMINAL_STATUSES)[number]

export const PRICING_MODELS = ['FLAT', 'USAGE_BASED', 'VOLUME_BASED'] as const
export type PricingModel = (typeof PRICING_MODELS)[number]

export const USAGE_PRICING_TYPES = ['PER_UNIT', 'TIME_OF_USE', 'TARIFF_PASS_THROUGH'] as const
export type UsagePricingType = (typeof USAGE_PRICING_TYPES)[number]

export type Granularity = 'DAY' | 'MONTH'
export type GroupBy = 'PRODUCT_TYPE' | 'USAGE_TYPE' | 'MEASURAND'

export type ReimbursementStatus =
  | 'FINALIZED'
  | 'INCOMPLETE'
  | 'REJECTED'
  | 'PENDING'
  | 'DRAFT'
  // Statuses mirrored from the payee's period reimbursement invoice once it is materialized at close.
  | 'READY_FOR_REVIEW'
  | 'OPEN'
  | 'PAID'
  | 'VOID'

export type PaymentOutcome = NonNullable<Payment['outcome']>
export const PAYMENT_OUTCOMES = [
  'SUCCEEDED',
  'PENDING',
  'CANCELLED',
  'FAILED',
  'BLOCKED'
] as const satisfies readonly PaymentOutcome[]

// --- product types (derived from ProductResponse) ---

export type ProductEntityType = EntitySelection['entityType']
export type ProductPricingType = Required<ProductResponse>['pricingType']
export type ProductType = Required<ProductResponse>['type']
export type ProductVisibility = NonNullable<ProductResponse['visibility']>
export const PRODUCT_VISIBILITIES: readonly ProductVisibility[] = ['PRIVATE', 'MARKETPLACE', 'PUBLIC']

export type ProductStatus = NonNullable<ProductResponse['status']>

export type VolumeTiersMode = NonNullable<VolumePricing['tiersMode']>
export const VOLUME_TIERS_MODES: readonly VolumeTiersMode[] = ['VOLUME', 'GRADUATED']

/** Tier quantity sources for VolumePricing. */
export type TierQuantitySource = VolumePricing['tierQuantitySource']
/** Sources offered at the product-pricing level; AGREEMENT_QUANTITY is agreement-level only. */
export const TIER_QUANTITY_SOURCES = ['ENTITY_COUNT', 'USAGE'] as const satisfies readonly TierQuantitySource[]

/**
 * Tier quantity sources selectable as a per-agreement-product override. Includes AGREEMENT_QUANTITY
 * (the quantity captured on the assignment), which is not offered at the product-pricing level where
 * no per-agreement quantity exists yet.
 */
export const AGREEMENT_TIER_QUANTITY_SOURCES: readonly TierQuantitySource[] = [
  'ENTITY_COUNT',
  'AGREEMENT_QUANTITY',
  'USAGE'
]

/**
 * Billing level for ENTITY_COUNT volume tiers: which infrastructure level the product's attached assets are
 * counted at when period close expands site/charger selections.
 */
export type EntityCountLevel = NonNullable<AgreementProductResponse['entityCountLevel']>
export const ENTITY_COUNT_LEVELS: readonly EntityCountLevel[] = ['CHARGER', 'CONNECTOR']

// --- agreement types (derived from AgreementResponse) ---

export type AgreementStatus = AgreementStatusV1

export type AgreementEntityType = AgreementAuthorizedEntityResponse['entityType']

/** Who, if anyone, collects payment for rated agreement usage. */
export type CollectionPolicy = NonNullable<AgreementResponse['collectionPolicy']>
export const COLLECTION_POLICIES: readonly CollectionPolicy[] = ['SYNOP_COLLECTS', 'EXTERNAL_COLLECTS', 'REPORTING_ONLY']

/**
 * How usage line items are rolled up into invoice items on period (non-real-time) invoices.
 * Resolved at invoice mint time: the attached invoice template's grouping wins when set, then the
 * agreement's own grouping, and finally SITE_PRICE ("{site} - {price}" lines) when neither defines
 * one. Never applies to real-time settlement, which is always itemized per session.
 */
export type LineItemGrouping = NonNullable<AgreementResponse['lineItemGrouping']>
export const LINE_ITEM_GROUPINGS: readonly LineItemGrouping[] = ['ATOMIC', 'PRODUCT', 'PRICE', 'SITE', 'PRODUCT_SITE', 'SITE_PRICE']

export type BillingCycle = NonNullable<AgreementResponse['billingCycle']>
export type AgreementCategory = NonNullable<AgreementResponse['category']>
export type AgreementType = NonNullable<AgreementResponse['type']>

/** How peak saver throttling is expressed: a fixed kW ceiling (POWER) or a percent reduction off the charger's max power (PERCENTAGE). */
export type PeakSaverSaveType = NonNullable<PeakSaverConfiguration['saveType']>
export const PEAK_SAVER_SAVE_TYPES: readonly PeakSaverSaveType[] = ['POWER', 'PERCENTAGE']

/** How a customer's share of a site's utility demand charge is measured at period close. */
export type DemandChargeAllocationMethod = NonNullable<DemandChargeAllocationConfiguration['allocationMethod']>
export const DEMAND_CHARGE_ALLOCATION_METHODS: readonly DemandChargeAllocationMethod[] = [
  'SITE_PEAK_CONTRIBUTION',
  'CUSTOMER_PEAK'
]

/** Language the agreement (and its generated invoices) is written in; drives translation of static invoice labels. */
export type AgreementLanguage = NonNullable<AgreementResponse['language']>
export const AGREEMENT_LANGUAGES: readonly AgreementLanguage[] = [
  'ENGLISH',
  'FRENCH',
  'GERMAN',
  'POLISH',
  'SPANISH',
  'DUTCH',
  'SWEDISH'
]
